import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface AnalyticsEvent {
  event_type: string;
  event_name: string;
  page_url?: string;
  metadata?: Record<string, any>;
}

interface SessionData {
  country_code?: string;
  country_name?: string;
  city?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
}

class AnalyticsTracker {
  private sessionId: string;
  private sessionStartTime: number;
  private lastEventTime: number;
  private pageCount: number = 1;
  private sessionData: SessionData = {};
  private initialized: boolean = false;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = Date.now();
    this.lastEventTime = Date.now();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  async initialize() {
    if (this.initialized) return;
    
    try {
      // Try multiple geolocation APIs with fallbacks
      let geoData = await this.getLocationData();
      
      if (geoData) {
        this.sessionData = {
          country_code: geoData.country_code,
          country_name: geoData.country_name,
          city: geoData.city,
          region: geoData.region,
          latitude: geoData.latitude,
          longitude: geoData.longitude,
        };
        console.log('Geolocation data obtained:', this.sessionData);
      } else {
        console.warn('No geolocation data available');
      }

      // Initialize session - this is critical
      await this.initializeSession();
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
      // Still try to initialize session even if geo fails
      try {
        await this.initializeSession();
      } catch (sessionError) {
        console.error('Failed to initialize session:', sessionError);
      }
      this.initialized = true; // Continue without geo data
    }
  }

  private async getLocationData(): Promise<SessionData | null> {
    // Try providers via Supabase Edge Function proxy to avoid CORS issues
    const ipapiRaw = await this.tryGeoViaProxy('ipapi');
    if (ipapiRaw) {
      return {
        country_code: ipapiRaw.country_code,
        country_name: ipapiRaw.country_name,
        city: ipapiRaw.city,
        region: ipapiRaw.region,
        latitude: ipapiRaw.latitude,
        longitude: ipapiRaw.longitude,
      };
    }

    const ipinfoRaw = await this.tryGeoViaProxy('ipinfo');
    if (ipinfoRaw) {
      return {
        country_code: ipinfoRaw.country,
        country_name: ipinfoRaw.country,
        city: ipinfoRaw.city,
        region: ipinfoRaw.region,
        latitude: ipinfoRaw.loc ? parseFloat(ipinfoRaw.loc.split(',')[0]) : undefined,
        longitude: ipinfoRaw.loc ? parseFloat(ipinfoRaw.loc.split(',')[1]) : undefined,
      };
    }

    const freegeoRaw = await this.tryGeoViaProxy('freegeoip');
    if (freegeoRaw) {
      return {
        country_code: freegeoRaw.country_code,
        country_name: freegeoRaw.country_name,
        city: freegeoRaw.city,
        region: freegeoRaw.region_name,
        latitude: freegeoRaw.latitude,
        longitude: freegeoRaw.longitude,
      };
    }

    const geopluginRaw = await this.tryGeoViaProxy('geoplugin');
    if (geopluginRaw) {
      return {
        country_code: geopluginRaw.geoplugin_countryCode,
        country_name: geopluginRaw.geoplugin_countryName,
        city: geopluginRaw.geoplugin_city,
        region: geopluginRaw.geoplugin_regionName,
        latitude: geopluginRaw.geoplugin_latitude ? parseFloat(geopluginRaw.geoplugin_latitude) : undefined,
        longitude: geopluginRaw.geoplugin_longitude ? parseFloat(geopluginRaw.geoplugin_longitude) : undefined,
      };
    }

    return null;
  }

  private async tryGeoAPI(url: string, transformer: (data: any) => SessionData): Promise<SessionData | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      console.log(`Trying geolocation API: ${url}`);
      
      const response = await fetch(url, { 
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        }
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`API ${url} response:`, data);
        
        if (data && !data.error) {
          const transformed = transformer(data);
          // Validate that we got meaningful data
          if (transformed.country_code || transformed.city) {
            console.log(`Successfully got location from ${url}:`, transformed);
            return transformed;
          }
        }
      } else {
        console.warn(`API ${url} returned status:`, response.status);
      }
    } catch (error) {
      console.warn(`API ${url} failed:`, error);
    }
    return null;
  }

  private async tryGeoViaProxy(provider: 'ipapi' | 'ipinfo' | 'freegeoip' | 'geoplugin'): Promise<any | null> {
    try {
      const { data, error } = await supabase.functions.invoke('geo-proxy', {
        body: { provider },
      });
      if (error) {
        console.warn(`Proxy ${provider} error:`, error);
        return null;
      }
      return (data as any)?.data ?? null;
    } catch (err) {
      console.warn(`Proxy ${provider} failed:`, err);
      return null;
    }
  }

  private async initializeSession() {
    const sessionData = {
      session_id: this.sessionId,
      first_page: window.location.pathname,
      last_page: window.location.pathname,
      ip_address: null, // Will be set by Supabase from request
      user_agent: navigator.userAgent,
      referrer: document.referrer || null,
      utm_source: new URLSearchParams(window.location.search).get('utm_source'),
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
      utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
      ...this.sessionData,
    };

    try {
      await supabase.from('analytics_sessions').insert(sessionData);
    } catch (error) {
      console.error('Failed to initialize session:', error);
    }
  }

  async trackEvent(event: AnalyticsEvent, userId?: string) {
    if (!this.initialized) {
      await this.initialize();
    }

    const eventData = {
      session_id: this.sessionId,
      event_type: event.event_type,
      event_name: event.event_name,
      page_url: event.page_url || window.location.href,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      user_id: userId,
      metadata: event.metadata || {},
      ...this.sessionData,
    };

    try {
      await supabase.from('analytics_events').insert(eventData);
      this.updateSession();
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  private async updateSession() {
    this.lastEventTime = Date.now();
    const duration = Math.floor((this.lastEventTime - this.sessionStartTime) / 1000);

    try {
      await supabase
        .from('analytics_sessions')
        .update({
          last_page: window.location.pathname,
          page_count: this.pageCount,
          duration_seconds: duration,
          last_seen_at: new Date().toISOString(),
        })
        .eq('session_id', this.sessionId);
    } catch (error) {
      console.error('Failed to update session:', error);
    }
  }

  async trackConversion(type: string, value: number = 0, userId?: string) {
    await this.trackEvent({
      event_type: 'conversion',
      event_name: type,
      metadata: { value }
    }, userId);

    // Mark session as converted
    try {
      await supabase
        .from('analytics_sessions')
        .update({
          converted: true,
          conversion_type: type,
          conversion_value: value,
        })
        .eq('session_id', this.sessionId);
    } catch (error) {
      console.error('Failed to mark conversion:', error);
    }
  }

  trackPageView(path?: string) {
    if (path) {
      this.pageCount++;
    }
    
    this.trackEvent({
      event_type: 'page_view',
      event_name: path || window.location.pathname,
      page_url: window.location.href,
    });
  }

  trackCTAClick(ctaName: string, location?: string) {
    this.trackEvent({
      event_type: 'cta_click',
      event_name: ctaName,
      metadata: { location }
    });
  }
}

// Global instance
let tracker: AnalyticsTracker | null = null;

export function useAnalytics() {
  const { user } = useAuth();
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current) {
      tracker = new AnalyticsTracker();
      tracker.initialize();
      initialized.current = true;

      // Track initial page view
      tracker.trackPageView();
    }
  }, []);

  const trackEvent = (event: AnalyticsEvent) => {
    if (tracker) {
      tracker.trackEvent(event, user?.id);
    }
  };

  const trackPageView = (path?: string) => {
    if (tracker) {
      tracker.trackPageView(path);
    }
  };

  const trackCTAClick = (ctaName: string, location?: string) => {
    if (tracker) {
      tracker.trackCTAClick(ctaName, location);
    }
  };

  const trackConversion = (type: string, value?: number) => {
    if (tracker) {
      tracker.trackConversion(type, value, user?.id);
    }
  };

  return {
    trackEvent,
    trackPageView,
    trackCTAClick,
    trackConversion,
  };
}